import { KeyboardArrowDown, KeyboardArrowRight, Search } from '@mui/icons-material';
import { Badge, Chip, Grid, InputAdornment, SvgIcon, Tooltip, Typography } from '@mui/material';
import {
  deleteAttachment,
  deletePurchaseOrderItem,
  getAllProjects,
  getPurchaseOrdersHistory,
  postAttachments,
  postPaymentSchedule,
  postPurchaseOrderItems,
  updatePurchaseOrder,
  updatePurchaseOrderItems
} from 'api';
import type {
  FileData,
  GetPurchaseOrders,
  PaymentPoDetails,
  PaymentScheduleForm,
  Project,
  PurchaseOrderBills,
  PurchaseOrderItem,
  SortConfig
} from 'api/types';
import Attachment from 'assets/images/attachment-icon.svg';
import EditIcon from 'assets/images/edit-icon.svg';
import AttachmentsTabModal from 'components/AttachmentsTabModal';
import type { DropdownOptionType } from 'components/inputs/Dropdown';
import DropDown from 'components/NewLayout/Dropdown';
import DataTable from 'components/NewLayout/Table';
import CustomTextfield from 'components/NewLayout/Textfield';
import UploadImage from 'components/UploadImage';
import UploadPurchaseOrderBlob from 'components/UploadPurchaseOrderBlob';
import UpdatePaymentScheduleModal from 'pages/PaymentWorkspace/component/UpdatePaymentScheduleModal';
import EditPoModal from 'pages/ProcurementWorkspace/components/EditPoModal';
import PropTypes from 'prop-types';
import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { defaultPageSize, orderByList } from 'utils/constants';
import {
  calculateItemsTotal,
  calculateModifiersTotal,
  currencyFormat,
  getPrice,
  getS3Path,
  sortArrByKey
} from 'utils/index';
import { v4 as uuidv4 } from 'uuid';

const paymentStatusList: DropdownOptionType[] = [
  {
    id: 'Paid',
    value: 'Paid'
  },
  {
    id: 'Partially Paid',
    value: 'Partially Paid'
  },
  {
    id: 'Not Paid',
    value: 'Not Paid'
  }
];

// Column mapping for backend API
const COLUMN_MAPPING = {
  purchase_order_number: 'po_number',
  requester: 'requester',
  vendor_name: 'vendor_name',
  total_price: 'total_amount',
  payment_status: 'payment_status',
  associatedjob: 'associated_job',
  created_at: 'created_at'
};

// Custom hook for debounced search
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Custom hook for search optimization
const useSearchOptimization = () => {
  const [searchInput, setSearchInput] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const debouncedSearch = useDebounce(searchInput, 300); // 300ms delay
  const searchCache = useRef<Map<string, any>>(new Map());
  const abortControllerRef = useRef<AbortController | null>(null);

  const clearCache = useCallback(() => {
    searchCache.current.clear();
  }, []);

  return {
    searchInput,
    setSearchInput,
    debouncedSearch,
    isSearching,
    setIsSearching,
    searchCache: searchCache.current,
    abortControllerRef,
    clearCache
  };
};

const PaymentStatusBadge = ({ status }) => {
  let color = '';
  let bgColor = '';

  switch (status) {
    case 'Paid':
      color = '#fff';
      bgColor = 'success.main';
      break;
    case 'Partially Paid':
      color = '#fff';
      bgColor = 'warning.main';
      break;
    case 'Not Paid':
      color = '#fff';
      bgColor = 'error.main';
      break;
    default:
      color = '#757575';
      bgColor = '#f5f5f5';
  }

  return (
    <Chip
      label={status}
      size="small"
      sx={{
        color,
        backgroundColor: bgColor,
        fontWeight: 500,
        '& .MuiChip-label': {
          paddingLeft: '12px',
          paddingRight: '12px'
        }
      }}
    />
  );
};

PaymentStatusBadge.propTypes = {
  status: PropTypes.string.isRequired
};

export default function PoHistory() {
  const [rowVisible, setRowVisible] = useState<number>(-1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [purchaseOrders, setPurchaseOrders] = useState<GetPurchaseOrders[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isUpdatePoModalVisible, setUpdatePoModalVisibility] = useState<boolean>(false);
  const [isUpdateScheduleModalVisible, setUpdateScheduleModalVisibility] = useState<boolean>(false);
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState<GetPurchaseOrders | null>();
  const [purchaseOrderItemsToBeDeletedList, setPurchaseOrderItemsToBeDeletedList] =
    useState<string[]>();
  const [allProjects, setAllProjects] = useState<DropdownOptionType[]>([]);
  const [dropdownSortDirection, setDropdownSortDirection] = useState<string>('DESC');
  const [paymentStatusDropdownValue, setPaymentStatusDropdownValue] = useState<string>('');
  const [isUpdateScheduleModalAfterPOVisible, setUpdateScheduleModalAfterPOVisibility] =
    useState<boolean>(false);
  const [newRows, setNewRows] = useState<PurchaseOrderItem[]>([]);
  const [isAttachmentsTabModalOpen, setIsAttachmentsTabModalOpen] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    column: 'created_at',
    direction: 'desc'
  });

  // Use the optimized search hook
  const {
    searchInput,
    setSearchInput,
    debouncedSearch,
    isSearching,
    setIsSearching,
    searchCache,
    abortControllerRef,
    clearCache
  } = useSearchOptimization();

  useEffect(() => {
    fetchProjects();
  }, []);

  // Unified data fetching effect with optimized caching
  useEffect(() => {
    const performDataFetch = async () => {
      // Create cache key including all parameters
      const cacheKey = `${debouncedSearch}-${currentPage}-${sortConfig.column}-${sortConfig.direction}-${paymentStatusDropdownValue}`;

      // Check cache first
      if (searchCache.has(cacheKey)) {
        const cachedResult = searchCache.get(cacheKey);
        setPurchaseOrders(cachedResult.data);
        setTotalCount(cachedResult.totalCount);
        return;
      }

      // Cancel previous request if still pending
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();

      try {
        setIsSearching(true);
        const result = await fetchPurchaseOrdersWithAbort(
          String(currentPage),
          defaultPageSize,
          debouncedSearch, // Always use debounced search term
          sortConfig.column,
          sortConfig.direction,
          paymentStatusDropdownValue
        );

        // Cache the result
        searchCache.set(cacheKey, result);
      } catch (error) {
        console.error('Data fetch error:', error);
      } finally {
        setIsSearching(false);
      }
    };

    performDataFetch();
  }, [debouncedSearch, sortConfig, paymentStatusDropdownValue, currentPage]);

  // Clear cache when filters change significantly
  useEffect(() => {
    clearCache();
  }, [paymentStatusDropdownValue, clearCache]);

  const fetchPurchaseOrdersWithAbort = async (
    pageNumber: string,
    pageSize: string,
    searchText = '',
    orderBy: string = sortConfig.column,
    orderDirection: string = sortConfig.direction,
    paymentStatus = ''
  ) => {
    const { data, totalCount } = await getPurchaseOrdersHistory(
      pageNumber.toString(),
      pageSize,
      searchText,
      orderBy,
      orderDirection,
      paymentStatus
    );

    const items = data.map((item) => ({
      ...item,
      item_count: item.purchase_order_items?.length.toString() || '0',
      subRows: item.purchase_order_items
    }));

    setPurchaseOrders(items);
    setTotalCount(parseInt(totalCount || '0'));
    setRowVisible(-1);

    return { data: items, totalCount: parseInt(totalCount || '0') };
  };

  const optimizedDataSource = useCallback(
    async (
      pageNumber: string,
      pageSize: string,
      searchText = '',
      orderBy: string = sortConfig.column,
      orderDirection: string = sortConfig.direction,
      paymentStatus = ''
    ) => {
      // Use current search term instead of passed searchText for pagination
      const currentSearchTerm = debouncedSearch || searchText;

      try {
        setIsLoading(true);
        await fetchPurchaseOrdersWithAbort(
          pageNumber,
          pageSize,
          currentSearchTerm, // Use current search context
          orderBy,
          orderDirection,
          paymentStatus || paymentStatusDropdownValue
        );
      } catch (error: Error) {
        handleError(error);
      } finally {
        setIsLoading(false);
      }
    },
    [debouncedSearch, paymentStatusDropdownValue, sortConfig]
  );

  const handleError = (error: Error) => {
    console.error(`Error: ${error.name} - ${error.message}`);
  };

  const toggleTr = (row, index) => {
    const RV = index === rowVisible ? -1 : index;
    setRowVisible(RV);
  };

  const fetchProjects = () => {
    getAllProjects()
      .then((projects) => {
        const sortedProjects: Project[] = sortArrByKey(
          projects
            .filter((p) => p.appearance_order_and_logistic && p.quickbooks_id)
            .map((p) => ({ ...p, project_code: parseInt(p.project_code) })),
          'project_code'
        );

        setAllProjects(
          sortedProjects.map((item) => ({
            id: item.project_id ?? '',
            value: `${item.project_code} - ${item.project_name}`
          }))
        );
      })
      .catch(handleError);
  };

  const onClickScheduleEdit = (row) => {
    setUpdateScheduleModalVisibility(true);
    setSelectedPurchaseOrder(row);
  };

  const onClickPoEdit = (row) => {
    setUpdatePoModalVisibility(true);
    setSelectedPurchaseOrder(row);
    setPurchaseOrderItemsToBeDeletedList([]);
  };

  const paymentPoDetails = () => {
    const newRowsTotal = calculateItemsTotal(newRows);
    const existingPurchaseOrderItemsTotal = calculateItemsTotal(
      selectedPurchaseOrder?.purchase_order_items
    );
    const total = Number(newRowsTotal) + Number(existingPurchaseOrderItemsTotal);

    const { shipping_cost, estimated_taxes, discount, deposit, refund } =
      selectedPurchaseOrder ?? {};

    const poCalcualtedTotal: number =
      total +
      parseFloat(shipping_cost || '0') +
      parseFloat(estimated_taxes ?? '0') -
      parseFloat(discount ?? '0') -
      parseFloat(deposit ?? '0') -
      parseFloat(refund ?? '0');

    const purchaseOrderData: PaymentPoDetails = {
      total_amount: poCalcualtedTotal,
      paid_amount: getPrice(selectedPurchaseOrder, 'total'),
      remaining_amount: poCalcualtedTotal - getPrice(selectedPurchaseOrder, 'total'),
      scheduled_amount: getPrice(selectedPurchaseOrder),
      po_number: selectedPurchaseOrder?.purchase_order_number || '',
      vendor_name: selectedPurchaseOrder?.vendor?.vendor_name || '',
      po_id: selectedPurchaseOrder?.purchase_order_id || ''
    };
    return purchaseOrderData;
  };

  const deletePurchaseOrderItem_ = async (purchaseOrderItemId: string) => {
    setPurchaseOrderItemsToBeDeletedList((prev = []) => [...prev, purchaseOrderItemId]);

    if (selectedPurchaseOrder) {
      const updatedPurchaseOrder = {
        ...selectedPurchaseOrder,
        purchase_order_items: selectedPurchaseOrder?.purchase_order_items?.filter(
          (item) => item.purchase_order_item_id !== purchaseOrderItemId
        )
      };

      setSelectedPurchaseOrder(updatedPurchaseOrder);
    }
  };

  const updatePurchaseOrder_ = async () => {
    const prevPurchaseOrdersState = [...purchaseOrders];
    const systemGeneratedPdFileName = `system-generated-pdf-${uuidv4()}.pdf`;

    try {
      setIsLoading(true);
      if (selectedPurchaseOrder) {
        if (newRows.length) {
          const newPurchaseOrderItems_: Partial<PurchaseOrderItem>[] = newRows.map((item) => ({
            item_name: item.item_name,
            quantity: item.quantity,
            unit_of_measure: item.unit_of_measure,
            price: item.price,
            project_id: item.project_id,
            description: item.description,
            suggested_vendor: '',
            purchase_order_id: selectedPurchaseOrder?.purchase_order_id
          }));

          await postPurchaseOrderItems(newPurchaseOrderItems_);
        }

        const existingItemsTotal = selectedPurchaseOrder?.purchase_order_items
          ? calculateItemsTotal(selectedPurchaseOrder.purchase_order_items)
          : 0;

        const newItemsTotal = newRows.reduce(
          (total, item) => total + Number(item.price) * Number(item.quantity),
          0
        );

        const modifierDetails = {
          shipping_cost: selectedPurchaseOrder?.shipping_cost,
          estimated_taxes: selectedPurchaseOrder?.estimated_taxes,
          discount: selectedPurchaseOrder?.discount,
          deposit: selectedPurchaseOrder?.deposit,
          refund: selectedPurchaseOrder?.refund
        };

        const modifiersTotal = calculateModifiersTotal(modifierDetails);

        const total_price = (
          Number(existingItemsTotal) +
          Number(modifiersTotal) +
          Number(newItemsTotal)
        ).toFixed(2);

        const purchaseOrderToUpdate: GetPurchaseOrders = {
          ...selectedPurchaseOrder,
          total_price,
          system_generated_pdf_s3_uri: getS3Path(
            systemGeneratedPdFileName,
            'system-generated-purchase-orders'
          )
        };

        purchaseOrderItemsToBeDeletedList?.length &&
          (await deletePurchaseOrderItem(purchaseOrderItemsToBeDeletedList));

        selectedPurchaseOrder?.purchase_order_items &&
          (await updatePurchaseOrderItems(selectedPurchaseOrder.purchase_order_items));

        await updatePurchaseOrder(purchaseOrderToUpdate, selectedPurchaseOrder.purchase_order_id);

        await UploadPurchaseOrderBlob({
          data: purchaseOrderToUpdate.purchase_order_items || [],
          vendorBillingData: purchaseOrderToUpdate.vendor || {},
          deliveryAddress: purchaseOrderToUpdate.delivery_address || '',
          poModifiers: {
            shipping_cost: purchaseOrderToUpdate.shipping_cost,
            estimated_taxes: purchaseOrderToUpdate.estimated_taxes,
            discount: purchaseOrderToUpdate.discount,
            deposit: purchaseOrderToUpdate.deposit,
            refund: purchaseOrderToUpdate.refund
          },
          specialInstructions: purchaseOrderToUpdate.instructions || '',
          poResponse: {
            purchase_order_id: purchaseOrderToUpdate.purchase_order_id,
            purchase_order_number: purchaseOrderToUpdate.purchase_order_number
          },
          requester: purchaseOrderToUpdate.requester || '',
          poDate: purchaseOrderToUpdate.created_at || new Date().toISOString(),
          fileName: systemGeneratedPdFileName,
          paymentTerms: purchaseOrderToUpdate.payment_terms || ''
        });

        // Clear cache after update to ensure fresh data
        clearCache();
      }
    } catch (error) {
      console.error('Error adding purchase order item:', error);
      setPurchaseOrders(prevPurchaseOrdersState);
    } finally {
      setIsLoading(false);
    }
    setUpdatePoModalVisibility(false);
  };

  const updatePaymentSchedule_ = async (paymentSchedule: PaymentScheduleForm[]) => {
    try {
      const purchaseOrderBills: PurchaseOrderBills = {
        paymentSchedules: paymentSchedule
      };

      await postPaymentSchedule(purchaseOrderBills, String(paymentPoDetails().po_id ?? ''));
    } catch (error) {
      toast.error('Error during bill update or deletion: ' + error, {
        position: 'top-center'
      });
    }
  };

  const getSetPurchseOrder = async () => {
    if (!selectedPurchaseOrder) return;
    const fetchUpdatedPurchaseOrder_ = await getPurchaseOrdersHistory(
      '1',
      defaultPageSize,
      selectedPurchaseOrder.purchase_order_id
    );

    const updatedPurchaseOrder = fetchUpdatedPurchaseOrder_.data[0];

    const updatedPurchaseOrders = purchaseOrders.map((po) =>
      po.purchase_order_id === selectedPurchaseOrder.purchase_order_id
        ? {
            ...updatedPurchaseOrder,
            subRows: updatedPurchaseOrder.purchase_order_items || []
          }
        : po
    );

    setPurchaseOrders(updatedPurchaseOrders);
  };

  const onSubmitPurchaseOrderAndPaymentSchedule = async (
    paymentSchedule: PaymentScheduleForm[]
  ) => {
    try {
      setIsLoading(true);

      if (selectedPurchaseOrder) {
        await updatePurchaseOrder_();
        await updatePaymentSchedule_(paymentSchedule);
        await getSetPurchseOrder();
      }

      toast.success('Bills updated successfully!', { position: 'top-center' });
    } catch (error) {
      console.error('Error updating bills:', error);
      toast.error('Failed to update bills. Please try again.', { position: 'top-center' });
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  const onSubmitPaymentSchedule = async (paymentSchedule: PaymentScheduleForm[]) => {
    try {
      setIsLoading(true);

      if (selectedPurchaseOrder) {
        await updatePaymentSchedule_(paymentSchedule);
        getSetPurchseOrder();
      }

      toast.success('Bills updated successfully!', { position: 'top-center' });
    } catch (error) {
      console.error('Error updating bills:', error);
      toast.error('Failed to update bills. Please try again.', { position: 'top-center' });
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  const getAssociatedJobs = (poItem: PurchaseOrderItem[]) => {
    const uniqueJobIds = [...new Set(poItem.map((item) => item.project_code))];
    return uniqueJobIds.join(', ');
  };

  // Memoize columns to prevent unnecessary re-renders
  const columns = useMemo(() => {
    const cols = [
      {
        name: '',
        prop: 'icons',
        isSort: false,
        data: (row, index) => {
          const i: number = parseInt(index?.toString()?.split('-')[0] || '-1');
          return (
            <div className="icon-div">
              <Tooltip title="Update Payment Schedule">
                <img
                  src={EditIcon}
                  onClick={() => onClickScheduleEdit(row)}
                  className="link-icon"
                  alt="Edit Icon"
                />
              </Tooltip>
              <Tooltip title={rowVisible === i ? 'Collapse' : 'Expand'}>
                <SvgIcon
                  sx={{ cursor: 'pointer', marginTop: '3px' }}
                  component={rowVisible == i ? KeyboardArrowDown : KeyboardArrowRight}
                  className={'link-icon'}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleTr(row, parseInt(i.toString()));
                  }}
                />
              </Tooltip>
              <Badge
                badgeContent={row.attachments?.length || 0}
                color="primary"
                invisible={!row.attachments?.length}
                sx={{
                  '& .MuiBadge-badge': {
                    right: 8,
                    top: 0,
                    fontSize: '0.65rem',
                    height: '16px',
                    minWidth: '16px',
                    padding: '0 4px'
                  }
                }}>
                <Tooltip title={`Attachments`}>
                  <img
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsAttachmentsTabModalOpen(true);
                      setSelectedPurchaseOrder(row);
                    }}
                    src={Attachment}
                    className={'img-fluid link-icon'}
                    width="30px"
                    style={{ cursor: 'pointer', display: 'block' }}
                  />
                </Tooltip>
              </Badge>
            </div>
          );
        }
      },
      {
        isSort: true,
        sortable: true,
        prop: 'purchase_order_number',
        name: 'PO Number',
        data: (row) => (
          <div className="icon-div">
            <Tooltip title="Update Purchase Order">
              <Typography
                sx={{
                  textDecoration: 'underline',
                  textDecorationThickness: '1px',
                  textUnderlineOffset: '3px',
                  cursor: 'pointer'
                }}
                onClick={() => onClickPoEdit(row)}>
                {row.purchase_order_number}
              </Typography>
            </Tooltip>
          </div>
        )
      },
      {
        isSort: true,
        sortable: true,
        prop: 'requester',
        name: 'Requester',
        data: (row) => row.requester || ''
      },
      {
        isSort: true,
        sortable: true,
        prop: 'vendor_name',
        name: 'Vendor',
        data: (row) => row.vendor?.vendor_name || ''
      },
      {
        isSort: true,
        sortable: true,
        prop: 'total_price',
        data: (row) => <Typography>{currencyFormat(row?.total_price || 0)}</Typography>,
        name: 'Total PO Amount'
      },
      {
        isSort: true,
        sortable: true,
        prop: 'payment_status',
        name: 'Payment Status',
        data: (row) => {
          const status = row.payment_status;
          return <PaymentStatusBadge status={status} />;
        }
      },
      {
        props: 'payment_terms',
        name: 'Payment Method',
        data: (row) => {
          const paymentTerms = row.payment_terms || '';
          return paymentTerms;
        }
      },
      {
        prop: 'credit_card',
        name: 'Credit Card',
        data: (row) => {
          const creditCard = row.credit_card.credit_card_display_name || 'N/A';
          return creditCard;
        }
      },
      {
        sortable: true,
        isSort: true,
        prop: 'associatedjob',
        name: 'Associated Job',
        data: (row) => getAssociatedJobs(row.purchase_order_items)
      },
      {
        prop: 'estimated_taxes',
        data: (row) => currencyFormat(row.estimated_taxes || '0'),
        name: 'Tax'
      },
      {
        prop: 'shipping_cost',
        data: (row) => currencyFormat(row.shipping_cost || '0'),
        name: 'Shipping'
      },
      {
        prop: 'discount',
        data: (row) => currencyFormat(row.discount || '0'),
        name: 'Discount'
      },
      {
        prop: 'deposit',
        data: (row) => currencyFormat(row.deposit || '0'),
        name: 'Deposit'
      },
      {
        prop: 'refund',
        data: (row) => currencyFormat(row.refund || '0'),
        name: 'Refund'
      },
      {
        prop: 'instructions',
        data: 'instructions',
        name: 'Instructions'
      },
      {
        isSort: true,
        sortable: true,
        prop: 'created_at',
        name: 'Created Date',
        type: 'date',
        dateFormat: 'date',
        data: 'created_at'
      }
    ];

    return cols;
  }, [rowVisible]);

  const subColumns = [
    {
      prop: 'project_name',
      isSort: false,
      data: (row) => `${row.project_code + ' - ' + row.project_name}`,
      name: 'Job Name'
    },
    {
      prop: 'item_name',
      isSort: false,
      data: 'item_name',
      name: 'Item Name'
    },
    {
      prop: 'price',
      data: (row) => currencyFormat(row.price || '0'),
      name: 'Price'
    },
    {
      prop: 'quantity',
      data: 'quantity',
      name: 'Quantity'
    },
    {
      prop: 'unit_of_measure',
      data: 'unit_of_measure',
      name: 'Unit Of Measure',
      isSort: false
    },
    {
      prop: 'description',
      data: 'description',
      name: 'Description',
      isSort: false
    }
  ];

  const handleSortDirectionChange = (event: ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value }
    } = event;
    setDropdownSortDirection(value);
    setSortConfig({
      column: 'created_at',
      direction: value.toLowerCase()
    });
  };

  const handlePaymentStatusChange = (event: ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value }
    } = event;
    setSortConfig({
      column: '',
      direction: ''
    });
    setPaymentStatusDropdownValue(value);
    setCurrentPage(0); // Reset to first page when filter changes
  };

  // Optimized search input handler
  const handleSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchInput(value);
      // Only reset to first page when search term actually changes (not on pagination)
      if (value !== debouncedSearch) {
        setCurrentPage(0);
      }
    },
    [setSearchInput, debouncedSearch]
  );

  const onConfirmAttachment = async (fileData: FileData) => {
    if (fileData.file && selectedPurchaseOrder) {
      try {
        const fileName = Date.now() + '_' + fileData.fileName.replaceAll(' ', '-');
        const myNewFile = new File([fileData.file], fileName, {
          type: fileData.file.type as string
        });
        const imagePath = getS3Path(fileName, 'purchase-order');

        await UploadImage(myNewFile, imagePath);

        await postAttachments(
          [imagePath],
          'PURCHASE_ORDER',
          selectedPurchaseOrder.purchase_order_id?.toString()
        );

        const updatedPurchaseOrders = purchaseOrders.map((po) =>
          po.purchase_order_id === selectedPurchaseOrder.purchase_order_id
            ? {
                ...po,
                attachments: [
                  ...(po.attachments || []),
                  {
                    id: `temp-${Math.floor(1000 + Math.random() * 9000)}`, // temporary ID until refetch
                    entity: 'PURCHASE_ORDER',
                    entity_id: selectedPurchaseOrder.purchase_order_id,
                    uri: imagePath,
                    created_at: new Date().toISOString(),
                    created_by: localStorage.getItem('userId') || '',
                    deleted_at: null
                  }
                ],
                subRows: po.purchase_order_items || []
              }
            : po
        );

        const dataToUpdate: GetPurchaseOrders = {
          purchase_order_id: selectedPurchaseOrder.purchase_order_id
        };

        await updatePurchaseOrder(dataToUpdate, selectedPurchaseOrder.purchase_order_id); // just to trigger UPDATE_BILL_ATTACHMENTS action type

        setPurchaseOrders(updatedPurchaseOrders);

        toast.success('Attachment uploaded successfully');
      } catch (error) {
        console.error('Error uploading attachment:', error);
        // toast.error('Failed to upload attachment');
      }
    }
  };

  const onDeleteAttachment = async (attachmentId: number | string) => {
    if (selectedPurchaseOrder && attachmentId) {
      try {
        // Check if this is a temporary ID
        const isTemporaryId = typeof attachmentId === 'string' && attachmentId.startsWith('temp-');

        // Only call the API if it's not a temporary ID
        if (!isTemporaryId) {
          await deleteAttachment(attachmentId as number);
        }

        const updatedPurchaseOrders = purchaseOrders.map((po) =>
          po.purchase_order_id === selectedPurchaseOrder.purchase_order_id
            ? {
                ...po,
                attachments:
                  po.attachments?.filter((attachment) => attachment.id !== attachmentId) || [],
                subRows: po.purchase_order_items || []
              }
            : po
        );

        const dataToUpdate: GetPurchaseOrders = {
          purchase_order_id: selectedPurchaseOrder.purchase_order_id
        };

        await updatePurchaseOrder(dataToUpdate, selectedPurchaseOrder.purchase_order_id); // just to trigger UPDATE_BILL_ATTACHMENTS action type

        setPurchaseOrders(updatedPurchaseOrders);

        toast.success('Attachment deleted successfully');
      } catch (error) {
        console.error('Error deleting attachment:', error);
        // Handle error - show error message
      }
    }
  };

  const onBackToEditPo = () => {
    setUpdateScheduleModalAfterPOVisibility(false);
    setUpdatePoModalVisibility(true);
  };

  const onClose = () => {
    setNewRows([]);
    setSelectedPurchaseOrder(null);
    setUpdatePoModalVisibility(false);
    setUpdateScheduleModalVisibility(false);
    setUpdateScheduleModalAfterPOVisibility(false);
  };

  // Cleanup function to abort pending requests
  useEffect(() => {
    () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4} sx={{ mb: 2 }}>
          <CustomTextfield
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SvgIcon component={Search} />
                </InputAdornment>
              )
            }}
            name="search"
            placeholder="Search something"
            value={searchInput}
            onChange={handleSearchChange}
          />
          {isSearching && (
            <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
              Searching...
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} sm={2} className="vendor-dropdown-tooltip">
          <DropDown
            inputName="order_direction"
            label="Order Direction"
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleSortDirectionChange(e)}
            options={orderByList}
            value={dropdownSortDirection}
          />
        </Grid>
        <Grid item xs={12} sm={2} className="vendor-dropdown-tooltip">
          <DropDown
            inputName="payment_status"
            label="Payment Status"
            onChange={(e: ChangeEvent<HTMLInputElement>) => handlePaymentStatusChange(e)}
            options={paymentStatusList}
            value={paymentStatusDropdownValue}
          />
        </Grid>
      </Grid>
      <DataTable
        items={purchaseOrders}
        columns={columns}
        subColumns={subColumns}
        totalCount={totalCount}
        serverSidePagination={true}
        dataSource={optimizedDataSource}
        loading={isLoading || isSearching}
        rowVisible={rowVisible}
        rowSelection
        currentPage={currentPage}
        setPageNo={(val) => setCurrentPage(val)}
        selectedRow={selectedPurchaseOrder}
        isSorting={true}
        handleSort={(order, orderBy) => {
          if (!COLUMN_MAPPING[orderBy]) return;
          setSortConfig({ column: COLUMN_MAPPING[orderBy], direction: order });
        }}
      />

      {isUpdatePoModalVisible && selectedPurchaseOrder && (
        <EditPoModal
          isOpen={isUpdatePoModalVisible}
          deletePurchaseOrderItem={deletePurchaseOrderItem_}
          purchaseOrder={selectedPurchaseOrder}
          setSelectedPurchaseOrder={setSelectedPurchaseOrder}
          onClose={onClose}
          allProjects={allProjects}
          newRows={newRows}
          setNewRows={setNewRows}
          onContinue={(purchaseOrderToUpdate) => {
            setUpdatePoModalVisibility(false);
            setUpdateScheduleModalAfterPOVisibility(true);
            setSelectedPurchaseOrder(purchaseOrderToUpdate);
          }}
        />
      )}

      {isUpdateScheduleModalAfterPOVisible && selectedPurchaseOrder && (
        <UpdatePaymentScheduleModal
          paymentPoDetails={paymentPoDetails()}
          purchaseOrder={selectedPurchaseOrder}
          onClose={() => onClose()}
          onBackToEditPo={onBackToEditPo}
          onComplete={(paymentSchedule: PaymentScheduleForm[]) =>
            onSubmitPurchaseOrderAndPaymentSchedule(paymentSchedule)
          }
        />
      )}

      {isUpdateScheduleModalVisible && selectedPurchaseOrder && (
        <UpdatePaymentScheduleModal
          paymentPoDetails={paymentPoDetails()}
          purchaseOrder={selectedPurchaseOrder}
          onClose={() => onClose()}
          onComplete={(paymentSchedule: PaymentScheduleForm[]) =>
            onSubmitPaymentSchedule(paymentSchedule)
          }
        />
      )}

      {isAttachmentsTabModalOpen && selectedPurchaseOrder && (
        <AttachmentsTabModal
          isOpen={isAttachmentsTabModalOpen}
          onClose={() => {
            setIsAttachmentsTabModalOpen(false);
            setSelectedPurchaseOrder(null);
          }}
          tableId="netvendor_grid_id"
          purchaseOrderData={selectedPurchaseOrder}
          attachments={selectedPurchaseOrder.attachments || []}
          onUpload={(fileData) => onConfirmAttachment(fileData)}
          onDelete={(id: number) => onDeleteAttachment(id)}
        />
      )}
    </>
  );
}
